/**
 * 團體實際行程／我的實際行程／個人後續行程的呈現模型。
 * 只把後端回應轉成使用者文案，不重算旅途事實；ID 一律維持字串，不經 Number。
 */
import { normalizeLocalDateTime, opaqueId } from './groupTripModel';
import { bookingCategoryLabel } from './disruptionModel';

const ID_PATTERN = /^[1-9]\d*$/;

export function isIdString(value) {
  return typeof value === 'string' && ID_PATTERN.test(value);
}

export function groupActualPath(groupTripId) {
  const id = opaqueId(groupTripId);
  return isIdString(id) ? `/group-trips/${id}/actual` : '';
}

export function travelerActualPath(groupTripId, travelerId) {
  const trip = opaqueId(groupTripId);
  const traveler = opaqueId(travelerId);
  return isIdString(trip) && isIdString(traveler) ? `/group-trips/${trip}/travelers/${traveler}/actual` : '';
}

export function journeyPlanningPath(journeyId) {
  const id = opaqueId(journeyId);
  return isIdString(id) ? `/journeys/${id}/planning` : '';
}

const pad2 = (value) => String(value).padStart(2, '0');

/** 後端 LocalDateTime（ISO 字串或陣列）轉為顯示文字；不做時區換算，缺值回傳空字串。 */
export function formatActualDateTime(value) {
  const local = normalizeLocalDateTime(value);
  const match = local.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/);
  if (!match) return '';
  return `${match[1]}/${Number(match[2])}/${Number(match[3])} ${match[4]}:${match[5]}`;
}

export function formatActualDate(value) {
  if (Array.isArray(value) && value.length >= 3) {
    return `${value[0]}/${Number(value[1])}/${Number(value[2])}`;
  }
  const match = String(value || '').match(/^(\d{4})-(\d{2})-(\d{2})/);
  return match ? `${match[1]}/${Number(match[2])}/${Number(match[3])}` : '';
}

export function formatActualTime(value) {
  if (Array.isArray(value) && value.length >= 2) return `${pad2(value[0])}:${pad2(value[1])}`;
  const match = String(value || '').match(/^(\d{2}):(\d{2})/);
  return match ? `${match[1]}:${match[2]}` : '';
}

const labelOf = (table, fallback) => (value) => (
  Object.prototype.hasOwnProperty.call(table, value) ? table[value] : fallback
);

export const PHASE_LABELS = Object.freeze({
  UPCOMING: '尚未出發',
  IN_PROGRESS: '旅途進行中',
  COMPLETED: '旅途已結束',
  CANCELLING: '取消處理中',
  CANCELLED: '已取消',
});
export const phaseLabel = labelOf(PHASE_LABELS, '狀態未提供');

export const ORIGIN_LABELS = Object.freeze({
  BASELINE: '出發時的計畫',
  RUNTIME_ADDED: '旅途中新增',
  RUNTIME_REPLACEMENT: '旅途中替換',
});
export const originLabel = labelOf(ORIGIN_LABELS, '活動');

export const ACTIVITY_STATE_LABELS = Object.freeze({
  SCHEDULED: '照常進行',
  CANCELLED: '已取消',
  REPLACED: '已被替換',
});
export const activityStateLabel = labelOf(ACTIVITY_STATE_LABELS, '狀態未提供');

export const TRAVELER_STATUS_LABELS = Object.freeze({
  ACTIVE: '同行中',
  LEFT: '已離團',
  REMOVED: '已移出',
});
export const travelerStatusLabel = labelOf(TRAVELER_STATUS_LABELS, '狀態未提供');

export const TIMELINE_EVENT_LABELS = Object.freeze({
  ACTIVITY_TIME_CHANGED: '調整活動時間',
  ACTIVITY_CANCELLED: '取消活動',
  ACTIVITY_ADDED: '新增活動',
  ACTIVITY_REPLACED: '替換活動',
});
export const timelineEventLabel = labelOf(TIMELINE_EVENT_LABELS, '旅途變更');

export const TRAVELER_EVENT_LABELS = Object.freeze({
  TRAVELER_PARTICIPATION_CHANGED: '活動參加狀態變更',
  INDEPENDENT_PERIOD_STARTED: '開始獨立行動',
  INDEPENDENT_PERIOD_ENDED: '結束獨立行動',
  GROUP_TRIP_PARTICIPATION_ENDED: '結束團體參與',
  TRAVELER_JOINED_LATE: '中途加入',
});
export const travelerEventLabel = labelOf(TRAVELER_EVENT_LABELS, '旅途紀錄');

export const PARTICIPATION_END_KIND_LABELS = Object.freeze({
  TRAVELER_REQUESTED_EXIT: '旅伴提出結束參與',
  HOST_OPERATIONAL_END: '主揪結束其參與',
});
export const participationEndKindLabel = labelOf(PARTICIPATION_END_KIND_LABELS, '已結束團體參與');

export const FULFILLMENT_LABELS = Object.freeze({
  DRAFT: '尚未確認',
  CONFIRMED: '已確認',
  COMPLETED: '已完成',
  CANCELLED: '已取消',
  DISRUPTED: '受異動影響',
});
export const fulfillmentLabel = labelOf(FULFILLMENT_LABELS, '狀態未提供');
export { bookingCategoryLabel };

const EFFECTIVE_LABELS = Object.freeze({
  EXPECTED: '預期參加',
  TEMPORARILY_AWAY: '暫時離開此活動',
  SKIPPED: '跳過此活動',
  ATTENDED: '已參加',
  NOT_PARTICIPATING_DUE_TO_INDEPENDENT_PERIOD: '不參加 · 獨立行動中',
  NOT_JOINED_YET: '加入之前',
});

const RECORDED_LABELS = Object.freeze({
  TEMPORARILY_AWAY: '暫時離開此活動',
  SKIPPED: '跳過此活動',
  ATTENDED: '已參加',
});

export function participationStateText(state) {
  if (state === 'NOT_PARTICIPATING') return '不在團體活動中';
  if (state === 'NOT_APPLICABLE') return '不適用';
  return EFFECTIVE_LABELS[state] || RECORDED_LABELS[state] || '';
}

export const UNRESOLVED_PARTICIPATION_COPY = '目前無法由團體的權威行程資料判定';
export const NOT_APPLICABLE_PARTICIPATION_COPY = '不適用（活動已取消或已被替換）';

/**
 * 參加狀態呈現。UNRESOLVED 與 NOT_APPLICABLE 是不同語意，皆不得轉成「未參加」等推論。
 * recordedState 為已記錄的明確狀態，與推導結果分開顯示。
 */
export function participationPresentation(row, travelerStatus) {
  const recorded = row?.recordedState && RECORDED_LABELS[row.recordedState]
    ? `已記錄：${RECORDED_LABELS[row.recordedState]}` : '';
  if (row?.resolution === 'UNRESOLVED_SCHEDULE_NOT_AUTHORITATIVE' || row?.effectiveState == null) {
    return { kind: 'unresolved', label: UNRESOLVED_PARTICIPATION_COPY, recorded };
  }
  if (row.effectiveState === 'NOT_APPLICABLE') {
    return { kind: 'not-applicable', label: NOT_APPLICABLE_PARTICIPATION_COPY, recorded };
  }
  if (row.effectiveState === 'NOT_PARTICIPATING') {
    return {
      kind: 'state',
      label: travelerStatus === 'ACTIVE' ? '已結束團體參與' : '已不在這趟揪團',
      recorded,
    };
  }
  return { kind: 'state', label: EFFECTIVE_LABELS[row.effectiveState] || '狀態未提供', recorded };
}

/**
 * 活動時間呈現。LIVE_DISPLAY_ONLY 只是補充顯示，不是旅途紀錄；UNAVAILABLE 不補任何時間。
 */
export function activitySchedulePresentation(activity) {
  const source = activity?.scheduleSource;
  if (source === 'UNAVAILABLE') {
    return { source: 'unavailable', text: '日期時間資訊目前無法取得', note: '' };
  }
  const date = formatActualDate(activity?.plannedDate);
  const start = formatActualTime(activity?.plannedStartTime);
  const end = formatActualTime(activity?.plannedEndTime);
  const time = start && end ? `${start}–${end}` : start || (end ? `至 ${end}` : '');
  const text = [date, time].filter(Boolean).join(' ') || '未排定時間';
  if (source === 'LIVE_DISPLAY_ONLY') {
    return { source: 'display-only', text, note: '時間僅供參考，取自目前行程，並非旅途紀錄' };
  }
  return { source: 'record', text, note: '' };
}

const SCOPE_COPY = Object.freeze({
  INDIVIDUAL_ONLY: '此異動影響範圍涉及個別旅伴',
  PARTY_ONLY: '此異動影響範圍涉及小隊',
  GROUP_WIDE: '此異動影響範圍涉及全團',
});

export function isPresentableDisruption(reference) {
  return Object.prototype.hasOwnProperty.call(SCOPE_COPY, reference?.impactScope);
}

/** 只有後端標記 travelerConfirmedAffected 的個人範圍，才可說此旅伴已確認受影響。 */
export function disruptionStatement(reference, { viewerIsSelf = false } = {}) {
  if (reference?.travelerConfirmedAffected === true && reference.impactScope === 'INDIVIDUAL_ONLY') {
    return viewerIsSelf ? '已確認此異動影響你' : '已確認此異動影響這位旅伴';
  }
  return SCOPE_COPY[reference?.impactScope] || '';
}

export const CONTINUATION_BLOCKED_PHASES = Object.freeze(['CANCELLING', 'CANCELLED']);

/**
 * 個人後續行程區塊。continuation 為 null 時（例如主揪檢視他人）不做任何推論，整區不顯示。
 */
export function continuationPresentation(view) {
  const continuation = view?.continuation;
  if (!view?.viewerIsSelf || continuation == null) return { mode: 'hidden' };
  if (continuation.hasPersonalContinuation) {
    return {
      mode: 'exists',
      journeyPath: journeyPlanningPath(continuation.createdJourneyId),
      startedAt: formatActualDateTime(continuation.continuationStartedAt),
    };
  }
  if (CONTINUATION_BLOCKED_PHASES.includes(view.phase)) return { mode: 'blocked' };
  return { mode: 'available' };
}

export const TITLE_MAX_LENGTH = 100;

export function toDateTimeInputValue(value) {
  return normalizeLocalDateTime(value) || '';
}

function comparableDateTime(value) {
  const local = Array.isArray(value)
    ? normalizeLocalDateTime(value)
    : String(value || '');
  const match = local.match(/^(\d{4}-\d{2}-\d{2}T\d{2}:\d{2})(?::(\d{2}))?/);
  return match ? `${match[1]}:${match[2] || '00'}` : '';
}

/** datetime-local（分鐘精度）轉為後端要求的秒精度 LocalDateTime 字串。 */
export function toRequestDateTime(inputValue) {
  return comparableDateTime(inputValue);
}

export function isBeforeEffectiveJoin(inputValue, effectiveJoinAt) {
  const start = comparableDateTime(inputValue);
  const join = comparableDateTime(Array.isArray(effectiveJoinAt) ? effectiveJoinAt : effectiveJoinAt || '');
  return Boolean(start && join && start < join);
}

/** 前端只擋明顯錯誤；資格與時間下限仍以後端回應為準。effectiveJoinAt 為 null 時不設任何下限。 */
export function validateContinuationDraft(draft, effectiveJoinAt) {
  const errors = {};
  const title = String(draft?.title ?? '').trim();
  if (!title) errors.title = '請填寫行程名稱。';
  else if (title.length > TITLE_MAX_LENGTH) errors.title = `行程名稱最多 ${TITLE_MAX_LENGTH} 字。`;
  if (!toRequestDateTime(draft?.continuationStartedAt)) {
    errors.continuationStartedAt = '請選擇個人行程的開始時間。';
  } else if (effectiveJoinAt && isBeforeEffectiveJoin(draft.continuationStartedAt, effectiveJoinAt)) {
    errors.continuationStartedAt = '開始時間不可早於你加入這趟揪團的時間。';
  }
  return errors;
}

export function buildContinuationRequest(draft, clientRequestId) {
  return {
    clientRequestId,
    continuationStartedAt: toRequestDateTime(draft.continuationStartedAt),
    title: String(draft.title).trim(),
  };
}

export function createContinuationRequestId() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `pc-${Date.now()}-${Math.random().toString(16).slice(2, 10)}`;
}

function errorCode(cause) {
  const code = cause?.code ?? cause?.raw?.code;
  if (code == null || code === '') return null;
  const value = Number(code);
  return Number.isInteger(value) ? value : null;
}

function httpStatus(cause) {
  return cause?.httpStatus ?? cause?.response?.status ?? null;
}

/**
 * 結果不明（連線中斷、逾時、伺服器錯誤）時必須沿用同一個 clientRequestId 重送，
 * 否則已成功的請求可能被誤判為「已建立過」。
 */
export function isUncertainOutcome(cause) {
  const status = httpStatus(cause);
  const code = errorCode(cause);
  if (status == null) return code == null || (code >= 500 && code < 600);
  return status >= 500;
}

export const CREATE_ERROR_COPY = Object.freeze({
  95160: '這位旅伴沒有綁定你的帳號，無法建立個人後續行程。',
  95161: '目前這趟揪團的狀態無法建立個人後續行程。',
  95162: '開始時間不可早於你加入這趟揪團的時間，請重新選擇。',
  95163: '你已經為這趟揪團建立過個人後續行程，每位旅伴只能建立一份。',
  95164: '這次送出的內容與先前未完成的送出不一致，系統沒有建立新行程。請確認內容後再重新送出。',
  95100: '內容不正確，請檢查行程名稱與開始時間。',
  95101: '你沒有建立此個人後續行程的權限。',
  95102: '找不到這趟揪團或旅伴。',
});

export const UNCERTAIN_CREATE_COPY = '無法確認是否已建立。請重試，重試會沿用相同內容，不會重複建立。';

export function createErrorPresentation(cause) {
  if (isUncertainOutcome(cause)) return { kind: 'uncertain', code: null, message: UNCERTAIN_CREATE_COPY };
  const code = errorCode(cause);
  if (code === 95163) return { kind: 'already-exists', code, message: CREATE_ERROR_COPY[code] };
  if (code === 95164) return { kind: 'conflict', code, message: CREATE_ERROR_COPY[code] };
  if (httpStatus(cause) === 401) return { kind: 'known', code, message: '登入已失效，請重新登入後再試。' };
  return {
    kind: 'known',
    code,
    message: CREATE_ERROR_COPY[code] || '目前無法建立個人後續行程，請稍後再試。',
  };
}

export function readErrorPresentation(cause) {
  const code = errorCode(cause);
  const status = httpStatus(cause);
  if (code === 95101 || status === 403) return { kind: 'forbidden', message: '你沒有權限查看這份實際行程。' };
  if (code === 95102 || status === 404) return { kind: 'not-found', message: '找不到這趟揪團或旅伴的實際行程。' };
  if (status === 401) return { kind: 'unauthenticated', message: '請先登入後再查看。' };
  if (code === 95100 || status === 400) return { kind: 'not-found', message: '找不到這趟揪團或旅伴的實際行程。' };
  if (status == null && code == null) return { kind: 'network', message: '網路連線異常，請稍後再試。' };
  return { kind: 'unexpected', message: '目前無法載入實際行程，請稍後再試。' };
}

/** 一般行程沒有揪團來源屬於正常情況（95165），不是錯誤。 */
export function isContinuationSourceAbsent(cause) {
  return errorCode(cause) === 95165
    || cause?.raw?.key === 'travel.grouptrip.personal_continuation.source_not_found';
}

export function provenancePresentation(source) {
  if (!source || !isIdString(opaqueId(source.sourceGroupTripId))) return null;
  return {
    groupActualPath: groupActualPath(source.sourceGroupTripId),
    startedAt: formatActualDateTime(source.continuationStartedAt),
    createdAt: formatActualDateTime(source.createdAt),
  };
}
